// P5/B2-frontend — typed fetch wrappers for the roles-permissions SPA + the
// effective-permissions role-gating feed.
//
// TWO server reads drive this feature (the matrix renders from SERVER data —
// the hardcoded iston RP_PERMISSIONS object is DELETED):
//   * GET /v1/me/effective-permissions — the CALLER's resolved (module, action,
//     allowed) cells. Drives role-gating across screens (show/hide actions) AND
//     supplies the canonical catalog AXES for the matrix (it enumerates every
//     MODULES x ACTIONS cell in catalog order, so the SPA needs no hardcoded
//     module/action list). The grid's dimensions are deliberately NOT written
//     down here: these comments carried a hard-coded module count that went
//     stale the moment `benefits` was appended, and MODULES is append-only.
//   * GET /v1/permission-profiles — the tenant's role bundles (the matrix BODY).
//     Each profile carries its granted permission_id[] which the SPA maps back
//     to a (module, action) cell via parsePermissionId.
//
// Profil yazımları ve membership hibeleri ayrı sözleşmelerdir: profil kaydı
// tek başına kimseye atama oluşturmaz; kritik membership hibesi 202 ile onaya gider.
// ADVISORY: effective-permissions gates the UI only — the server re-checks every
// call (ADR-017; never a trust boundary).
//
// Explicit adapter signatures below describe each consumed wire contract.
// Generated OpenAPI paths exist; request/response metadata varies by endpoint,
// so these casts retain the local DTO boundary.

import { platformGraph } from "@/api/platform-graph";
import type { paths } from "@/api/schema.generated";
import { unwrapEnvelope, type EnvelopedFetchResult } from "@/api/errors";

// --------------------------------------------------------------------------
// Wire shapes (mirror the route DTOs exactly).
// --------------------------------------------------------------------------

/** One resolved (module, action) cell from GET /v1/me/effective-permissions. */
export interface EffectiveCell {
  readonly module: string;
  readonly action: string;
  readonly allowed: boolean;
}

export interface EffectivePermissionsResponse {
  readonly permissions: readonly EffectiveCell[];
  readonly source_profile_ids: readonly string[];
}

export type PermissionProfilesResponse = paths["/v1/permission-profiles"]["get"]["responses"][200]["content"]["application/json"]["data"];
export type PermissionProfile = PermissionProfilesResponse["profiles"][number];
export type PermissionCatalogEntry = PermissionProfilesResponse["catalog"][number];
export type CreatePermissionProfile = paths["/v1/permission-profiles"]["post"]["requestBody"]["content"]["application/json"];
export type PatchPermissionProfile = paths["/v1/permission-profiles/{profileId}"]["patch"]["requestBody"]["content"]["application/json"];

export async function createPermissionProfile(body: CreatePermissionProfile, idempotencyKey: string): Promise<PermissionProfile> {
  const result = await unwrapEnvelope(platformGraph.POST("/v1/permission-profiles", { body, params: { header: { "x-idempotency-key": idempotencyKey } } }));
  return result.profile;
}

export async function patchPermissionProfile(profileId: string, body: PatchPermissionProfile): Promise<PermissionProfile> {
  const result = await unwrapEnvelope(platformGraph.PATCH("/v1/permission-profiles/{profileId}", { body, params: { path: { profileId } } }));
  return result.profile;
}

// --------------------------------------------------------------------------
// Fetchers use explicit wire signatures for the consumed contracts.
// --------------------------------------------------------------------------

type EffectiveGet = (
  path: "/v1/me/effective-permissions",
) => Promise<EnvelopedFetchResult<EffectivePermissionsResponse>>;

/**
 * Fetch the caller's own resolved effective permissions (advisory — drives the
 * UI gating + supplies the matrix axes). Returns the full catalog grid (every MODULES x ACTIONS cell).
 */
export async function fetchEffectivePermissions(): Promise<EffectivePermissionsResponse> {
  const get = platformGraph.GET as unknown as EffectiveGet;
  return unwrapEnvelope(get("/v1/me/effective-permissions"));
}

/** Profil ve düzenlenebilir hücreler aynı yetkili registry yanıtından gelir. */
export async function fetchPermissionProfiles(): Promise<PermissionProfilesResponse> {
  return unwrapEnvelope(platformGraph.GET("/v1/permission-profiles"));
}

// --------------------------------------------------------------------------
// permission_id ↔ (module, action) — the SAME convention the server catalog
// uses (perm_<module>_<action>). Parsed client-side so the matrix maps a
// profile's permission_id[] onto the cell grid WITHOUT a hardcoded matrix.
// --------------------------------------------------------------------------

/**
 * Parse `perm_<module>_<action>` → { module, action }, or null when the id is
 * not a catalog cell (a legacy/off-grid permission). The action is the LAST
 * segment; the module is everything between `perm_` and the action (modules can
 * contain underscores, e.g. `time_attendance`, `approval_flows`).
 */
export function parsePermissionId(
  permissionId: string,
  actions: readonly string[],
): { module: string; action: string } | null {
  if (!permissionId.startsWith("perm_")) return null;
  const rest = permissionId.slice("perm_".length);
  // Match the action as a known suffix so a multi-word module is preserved.
  for (const action of actions) {
    const suffix = `_${action}`;
    if (rest.endsWith(suffix)) {
      const module = rest.slice(0, rest.length - suffix.length);
      if (module.length > 0) return { module, action };
    }
  }
  return null;
}

// --------------------------------------------------------------------------
// SEM-7121 — role MEMBERSHIP reads + writes (the assignment surface).
//
// The screen shows PROFILES; entity_role_membership is keyed by ROLE. The two
// are joined by the profile's `target_kind: "role"` assignment, which
// provisioning writes for every cloned archetype (cloneRbacDefaults). So the
// screen reads the selected profile's assignments FIRST to learn which role it
// hands out, and only then lists/edits that role's members. A profile bound to
// no role has no member surface — there is nothing a membership row could
// grant it.
// --------------------------------------------------------------------------

/** One row of GET /v1/role-memberships (route: role-memberships.ts). */
export interface RoleMembership {
  readonly role_membership_id: string;
  readonly employee_id: string;
  readonly employee_name: string | null;
  readonly role_id: string;
  readonly granted_at: string;
  readonly granted_by: string;
  readonly revoked_at: string | null;
}

export interface RoleMembershipsResponse {
  readonly role_id: string;
  readonly memberships: readonly RoleMembership[];
  /** Pre-cap total — the page itself is capped server-side. */
  readonly total_count: number;
}

type AssignmentsGet = (
  path: "/v1/permission-assignments",
  init: { params: { query: { profile_id: string; limit: number } } },
) => Promise<
  EnvelopedFetchResult<{
    readonly assignments: readonly {
      readonly target_kind: string;
      readonly target_spec: Record<string, unknown>;
      readonly effective_to: string | null;
    }[];
  }>
>;

/**
 * The role ids the given profile is bound to (live bindings only). Usually
 * exactly one; the array is what the endpoint can honestly return, since
 * nothing stops a tenant binding a profile to two roles.
 */
export async function fetchProfileRoleIds(profileId: string): Promise<readonly string[]> {
  const get = platformGraph.GET as unknown as AssignmentsGet;
  const page = await unwrapEnvelope(
    get("/v1/permission-assignments", { params: { query: { profile_id: profileId, limit: 50 } } }),
  );
  const roleIds: string[] = [];
  for (const a of page.assignments) {
    if (a.target_kind !== "role" || a.effective_to !== null) continue;
    const roleId = a.target_spec["role_id"];
    if (typeof roleId === "string" && !roleIds.includes(roleId)) roleIds.push(roleId);
  }
  return roleIds;
}

type MembershipsGet = (
  path: "/v1/role-memberships",
  init: { params: { query: { role_id: string } } },
) => Promise<EnvelopedFetchResult<RoleMembershipsResponse>>;

/** Live members of a role. Server-gated settings.read. */
export async function fetchRoleMemberships(roleId: string): Promise<RoleMembershipsResponse> {
  const get = platformGraph.GET as unknown as MembershipsGet;
  return unwrapEnvelope(get("/v1/role-memberships", { params: { query: { role_id: roleId } } }));
}

type MembershipPost = (
  path: "/v1/role-memberships",
  init: { body: { employee_id: string; role_id: string } },
) => Promise<
  EnvelopedFetchResult<
    | { membership: RoleMembership; created: boolean }
    | {
        approval_request_id: string;
        role_membership_id: string;
        critical_permission_ids: readonly string[];
      }
  >
>;

/**
 * The two ways a grant can end (SEM-8105). A role whose bound profiles carry an
 * is_critical cell does NOT write a membership: the server opens an approval and
 * answers 202, and the person is not in the role until someone decides it. The
 * caller has to tell the two apart — reporting "added" for a pending approval is
 * the screen lying about a permission change.
 */
export type GrantRoleMembershipResult =
  | { readonly kind: "granted"; readonly membership: RoleMembership }
  | { readonly kind: "approval_pending" };

/** Put a person in a role. Idempotent — a second grant returns the live row. */
export async function grantRoleMembership(
  employeeId: string,
  roleId: string,
): Promise<GrantRoleMembershipResult> {
  const post = platformGraph.POST as unknown as MembershipPost;
  const result = await unwrapEnvelope(
    post("/v1/role-memberships", { body: { employee_id: employeeId, role_id: roleId } }),
  );
  return "membership" in result
    ? { kind: "granted", membership: result.membership }
    : { kind: "approval_pending" };
}

type MembershipDelete = (
  path: "/v1/role-memberships/{employeeId}/{roleId}",
  init: { params: { path: { employeeId: string; roleId: string } } },
) => Promise<EnvelopedFetchResult<{ revoked_at: string | null; revoked: boolean }>>;

/**
 * Take a person out of a role. The row is NOT deleted — the server stamps
 * revoked_at (0195), so the grant stays auditable.
 */
export async function revokeRoleMembership(employeeId: string, roleId: string): Promise<void> {
  const remove = platformGraph.DELETE as unknown as MembershipDelete;
  await unwrapEnvelope(
    remove("/v1/role-memberships/{employeeId}/{roleId}", {
      params: { path: { employeeId, roleId } },
    }),
  );
}
